/**
 * The density textures of a view's FogVolumes, stacked in depth into one 3D texture. A WebGL2
 * shader indexes no sampler array in a loop, so the light pass samples each volume's region of it.
 */
import * as THREE from 'three';
import * as logger from '../../logger.js';
import { dataTexture3D } from '../../resources/textures/dataTexture3D.js';
import { MAX_3D_TEXTURE_EXTENT } from '../webglLimits.js';
export class FogDensityAtlas {
    texture = dataTexture3D(new Uint8Array(1), 1, 1, 1, THREE.RedFormat);
    /** Written by `update`: the distinct textures the stack holds, in order. */
    stacked = [];
    regions = new Map();
    /**
     * Stacks `textures` again when they are not the set the stack holds, and answers whether they
     * were not. The order does not count, so a volume added or removed elsewhere restacks nothing.
     */
    update(textures) {
        if (this.holdsExactly(textures))
            return false;
        this.stacked = [...new Set(textures)];
        this.restack();
        return true;
    }
    /** The region `texture` holds, or nothing for a texture the stack left out. */
    regionOf(texture) {
        return this.regions.get(texture);
    }
    dispose() {
        this.texture.dispose();
    }
    /** Indexed, not `.every`: it runs on every render, so it allocates nothing. */
    holdsExactly(textures) {
        for (let i = 0; i < textures.length; i++) {
            if (!this.stacked.includes(textures[i]))
                return false;
        }
        for (let i = 0; i < this.stacked.length; i++) {
            if (!textures.includes(this.stacked[i]))
                return false;
        }
        return true;
    }
    /** A float density can pass 1, which a byte stack would clamp. */
    holdsFloats() {
        for (const texture of this.regions.keys())
            if (texture.type === THREE.HalfFloatType)
                return true;
        return false;
    }
    restack() {
        this.regions.clear();
        let depth = 0;
        for (const texture of this.stacked) {
            const { width, height, depth: slices } = texture.image;
            if (depth + slices > MAX_3D_TEXTURE_EXTENT) {
                logger.warn(`[Fog] A density texture past ${MAX_3D_TEXTURE_EXTENT} stacked slices is not sampled`);
                continue;
            }
            this.regions.set(texture, { width, height, depth: slices, zStart: depth });
            depth += slices;
        }
        const regions = [...this.regions.values()];
        const width = Math.max(1, ...regions.map((region) => region.width));
        const height = Math.max(1, ...regions.map((region) => region.height));
        const texelCount = width * height * Math.max(1, depth);
        const data = this.holdsFloats() ? new Float32Array(texelCount) : new Uint8Array(texelCount);
        for (const [texture, region] of this.regions)
            copyInto(data, width, height, texture, region);
        this.texture.dispose();
        this.texture = dataTexture3D(data, width, height, Math.max(1, depth), THREE.RedFormat);
    }
}
/**
 * Copies the red channel of each row of `texture` into its region of the stack, whose rows are
 * `width` texels long. A density reads only red (`scene/resources/3d/fog_material.cpp:161`).
 */
function copyInto(data, width, height, texture, region) {
    const read = texelReader(texture, data instanceof Float32Array);
    const stride = texture.format === THREE.RedFormat ? 1 : 4;
    for (let z = 0; z < region.depth; z++) {
        for (let y = 0; y < region.height; y++) {
            const source = (z * region.height + y) * region.width;
            const target = ((region.zStart + z) * height + y) * width;
            for (let x = 0; x < region.width; x++)
                data[target + x] = read((source + x) * stride);
        }
    }
}
/** A texel of `texture` as the stack stores it: a byte as is, else the value the GPU samples. */
function texelReader(texture, isFloatStack) {
    const texels = texture.image.data;
    if (texture.type === THREE.HalfFloatType)
        return (index) => THREE.DataUtils.fromHalfFloat(texels[index]);
    return isFloatStack ? (index) => texels[index] / 255 : (index) => texels[index];
}
//# sourceMappingURL=fogDensityAtlas.js.map